import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  maxTags?: number;
}

const PREDEFINED_TAGS = {
  themes: [
    'Coming of Age',
    'Family',
    'Friendship',
    'Love',
    'Loss',
    'Identity',
    'Power',
    'Justice',
    'Survival',
    'Redemption',
    'Revenge',
    'War',
    'Politics',
    'Religion',
    'Class',
    'Race',
    'Gender',
    'Nature',
  ],
  moods: [
    'Dark',
    'Light',
    'Emotional',
    'Funny',
    'Tense',
    'Cozy',
    'Inspiring',
    'Heartbreaking',
    'Atmospheric',
    'Fast-paced',
    'Slow-burn',
    'Twisty',
    'Thought-provoking',
    'Escapist',
    'Gritty',
    'Whimsical',
  ],
};

export function TagSelector({ selectedTags, onChange, maxTags = 5 }: TagSelectorProps) {
  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onChange(selectedTags.filter(t => t !== tag));
    } else if (selectedTags.length < maxTags) {
      onChange([...selectedTags, tag]);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium mb-2 block">
          Themes & Moods (Optional, select up to {maxTags})
        </Label>
        <p className="text-xs text-muted-foreground mb-3">
          Help readers find books with similar vibes
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Themes</h4>
          <div className="flex flex-wrap gap-2">
            {PREDEFINED_TAGS.themes.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              const isDisabled = !isSelected && selectedTags.length >= maxTags;
              
              return (
                <Badge
                  key={tag}
                  variant={isSelected ? 'default' : 'outline'}
                  className={cn(
                    'cursor-pointer transition-all',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => !isDisabled && toggleTag(tag)}
                >
                  {tag}
                </Badge>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-2">Moods</h4>
          <div className="flex flex-wrap gap-2">
            {PREDEFINED_TAGS.moods.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              const isDisabled = !isSelected && selectedTags.length >= maxTags;
              
              return (
                <Badge
                  key={tag}
                  variant={isSelected ? 'default' : 'outline'}
                  className={cn(
                    'cursor-pointer transition-all',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => !isDisabled && toggleTag(tag)}
                >
                  {tag}
                </Badge>
              );
            })}
          </div>
        </div>
      </div>

      {selectedTags.length > 0 && (
        <div className="pt-2">
          <p className="text-xs text-muted-foreground">
            Selected: {selectedTags.length}/{maxTags}
          </p>
        </div>
      )}
    </div>
  );
}
